import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench, Sparkles, Building2, Home, ChefHat, Bath, ChevronDown, ArrowUpRight, Check } from "lucide-react";
import { IMAGES, ELEMENT_WRENCH, ELEMENT_SPRAY } from "@/lib/site";
import { Reveal } from "@/components/motion/Reveal";

const services = [
  {
    id: "handyman",
    icon: Wrench,
    kicker: "Led by André",
    title: "Handyman & Repairs",
    service: "Handyman & Repairs",
    desc: "Careful, reliable fixes and improvements for your home — done right the first time.",
    points: ["Small repairs", "Flooring replacement", "Interior & exterior painting", "General maintenance"],
    img: IMAGES.handyman,
    theme: "bg-petrol text-offwhite",
    overlay: "bg-petrol/50",
  },
  {
    id: "residential-cleaning",
    icon: Sparkles,
    kicker: "Led by his wife & daughter",
    title: "Residential Cleaning",
    service: "Residential Cleaning",
    desc: "A spotless, welcoming home — thorough, detailed cleaning for houses and apartments.",
    points: ["Homes & apartments", "Kitchens & bathrooms", "Move-out cleaning", "Detailed & dependable"],
    img: IMAGES.cleaning,
    theme: "bg-graphite text-offwhite",
    overlay: "bg-graphite/50",
  },
  {
    id: "commercial-cleaning",
    icon: Building2,
    kicker: "For businesses & offices",
    title: "Commercial Cleaning",
    service: "Commercial Cleaning",
    desc: "Clean, professional spaces for your business — offices, storefronts and commercial spaces.",
    points: ["Offices & storefronts", "Commercial spaces", "Recurring schedules", "Reliable & professional"],
    img: IMAGES.commercial,
    theme: "bg-ink text-offwhite",
    overlay: "bg-ink/55",
  },
];

const handymanCategories = [
  {
    icon: Home,
    title: "Inside the Home",
    items: [
      "Cabinet and cupboard installation",
      "Shelf installation",
      "Door and doorknob installation",
      "Drywall repair",
      "Painting and touch-ups",
      "Baseboard and trim installation",
      "Crown molding installation",
      "Furniture assembly",
      "Curtain and blind installation",
      "TV mounting and wall bracket installation",
      "Small wall and ceiling repairs",
    ],
  },
  {
    icon: ChefHat,
    title: "Kitchen",
    items: [
      "Cabinet installation",
      "Backsplash installation",
      "Handle/hardware installation",
      "Door and drawer adjustment",
      "Small countertop repairs",
    ],
  },
  {
    icon: Bath,
    title: "Bathroom",
    items: [
      "Vanity/cabinet installation",
      "Mirror installation",
      "Accessory installation",
      "Drywall repair",
      "Door and trim installation",
      "Small finishing work",
    ],
  },
];

const requestService = (service) => {
  window.dispatchEvent(new CustomEvent("select-service", { detail: service }));
  const el = document.getElementById("contact");
  if (!el) return;
  if (window.lenis) window.lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Services() {
  const [showHandyman, setShowHandyman] = useState(false);
  return (
    <section id="services" className="relative py-20 md:py-32 bg-offwhite overflow-hidden" data-testid="services-section">
      <img src={ELEMENT_WRENCH} alt="" aria-hidden="true" className="pointer-events-none select-none absolute -left-16 top-28 w-72 opacity-[0.05] -rotate-12 hidden lg:block" />
      <img src={ELEMENT_SPRAY} alt="" aria-hidden="true" className="pointer-events-none select-none absolute -right-8 -bottom-6 w-60 opacity-[0.06] rotate-6 hidden lg:block" />
      <div className="max-w-7xl mx-auto px-5 lg:px-12 relative z-10">
        <Reveal className="max-w-2xl mb-14 md:mb-20">
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-petrol mb-4">Our Services</p>
          <h2 className="font-display font-extrabold text-ink text-3xl md:text-5xl tracking-tight leading-tight">
            Two service lines, <span className="text-graphite">one family standard.</span>
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((s, idx) => (
            <Reveal key={s.id} delay={idx * 0.12}>
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative overflow-hidden rounded-[28px] ${s.theme} h-full flex flex-col`}
                data-testid={`service-card-${s.id}`}
              >
                <div className="relative h-52 md:h-56 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="h-full w-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-85 transition-[transform,opacity] duration-700"
                  />
                  <div className={`absolute inset-0 ${s.overlay}`} />
                  <div className="absolute top-5 left-5 h-12 w-12 rounded-2xl bg-mustard flex items-center justify-center shadow-lg">
                    <s.icon className="h-6 w-6 text-ink" strokeWidth={2.2} />
                  </div>
                </div>

                <div className="p-7 md:p-8 flex flex-col flex-1">
                  <p className="text-xs font-semibold tracking-[0.15em] uppercase text-mustard mb-3">{s.kicker}</p>
                  <h3 className="font-display font-bold text-2xl leading-tight mb-3">{s.title}</h3>
                  <p className="text-offwhite/75 font-light leading-relaxed mb-6">{s.desc}</p>

                  <ul className="space-y-3 mb-8">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-offwhite/90">
                        <Check className="h-4 w-4 mt-0.5 shrink-0 text-mustard" strokeWidth={2.5} />
                        {p}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => requestService(s.service)}
                    data-testid={`service-cta-${s.id}`}
                    className="mt-auto inline-flex items-center gap-2 self-start rounded-full bg-mustard px-6 py-3 text-sm font-semibold text-ink hover:gap-3 transition-[gap] duration-300"
                  >
                    Request This Service
                    <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
                  </button>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        {/* Handyman & Repairs — detailed breakdown */}
        <div className="mt-8 md:mt-12">
          <button
            type="button"
            onClick={() => setShowHandyman((v) => !v)}
            data-testid="handyman-details-toggle"
            aria-expanded={showHandyman}
            className="group flex items-center gap-3 rounded-2xl border border-ink/10 bg-white px-5 md:px-6 py-4 w-full hover:border-mustard/60 transition-colors duration-300"
          >
            <span className="h-9 w-9 rounded-xl bg-petrol flex items-center justify-center shrink-0">
              <Wrench className="h-4 w-4 text-offwhite" strokeWidth={2.2} />
            </span>
            <span className="font-display font-bold text-ink text-base md:text-lg text-left">
              Explore all Handyman &amp; Repairs services
            </span>
            <ChevronDown className={`h-5 w-5 text-petrol ml-auto transition-transform duration-300 ${showHandyman ? "rotate-180" : ""}`} />
          </button>

          <AnimatePresence initial={false}>
            {showHandyman && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
                data-testid="handyman-details-panel"
              >
                <div className="grid md:grid-cols-3 gap-5 lg:gap-6 pt-6">
                  {handymanCategories.map((c) => (
                    <div key={c.title} className="rounded-[24px] border border-ink/10 bg-white p-6 md:p-7" data-testid={`handyman-cat-${c.title.toLowerCase().replace(/\s/g, "-")}`}>
                      <div className="flex items-center gap-3 mb-5">
                        <span className="h-11 w-11 rounded-2xl bg-mustard flex items-center justify-center shrink-0">
                          <c.icon className="h-5 w-5 text-ink" strokeWidth={2.2} />
                        </span>
                        <h4 className="font-display font-bold text-ink text-lg">{c.title}</h4>
                      </div>
                      <ul className="space-y-2.5">
                        {c.items.map((it) => (
                          <li key={it} className="flex items-start gap-2 text-sm text-ink/70 font-light">
                            <Check className="h-4 w-4 mt-0.5 shrink-0 text-mustard" strokeWidth={2.5} />
                            {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => requestService("Handyman & Repairs")}
                  data-testid="handyman-details-cta"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-petrol px-7 py-3.5 text-sm font-semibold text-offwhite hover:bg-ink hover:gap-3 transition-[gap,background-color] duration-300"
                >
                  Request This Service
                  <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
